import { useEffect, useState } from "react";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - vite-plugin-pwa virtual module
import { useRegisterSW } from "virtual:pwa-register/react";

/**
 * Service-worker update banner. Appears at the bottom of the viewport when
 * a new SW version is available and waiting to activate. The user must
 * tap "Vernieuwen" to swap — never auto-swaps mid-event.
 *
 * `registerType: "prompt"` in vite.config.ts ensures the SW does not
 * skip-waiting on its own; this component drives the swap explicitly.
 */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url: string, r: ServiceWorkerRegistration | undefined) {
      // Periodically poll for updates (every 30 min). Cheap; just a HEAD
      // request to the SW URL. Without this the prompt only appears on
      // page reload after a deploy.
      if (r) {
        setInterval(
          () => {
            r.update().catch(() => {
              /* offline or no new version — ignore */
            });
          },
          30 * 60 * 1000,
        );
      }
    },
  });

  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (needRefresh) setDismissed(false);
  }, [needRefresh]);

  if (!needRefresh || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="App-update beschikbaar"
      className="fixed bottom-24 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-1.5rem)]"
    >
      <div
        className="card-elevated px-4 py-3 flex items-center gap-3"
        style={{ borderColor: "rgba(255, 107, 31, 0.4)" }}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulseSoft shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 font-medium">
            Nieuwe versie beschikbaar
          </div>
          <div className="text-2xs text-zinc-500 mt-0.5">
            Refresh laadt de update — niets gaat verloren.
          </div>
        </div>
        <button
          onClick={() => {
            setDismissed(true);
            setNeedRefresh(false);
          }}
          className="px-2.5 py-1.5 rounded-md text-2xs text-zinc-400 hover:text-zinc-200"
        >
          Later
        </button>
        <button
          onClick={() => updateServiceWorker(true)}
          className="px-3 py-2 rounded-lg bg-accent text-ink-950 font-semibold text-2xs"
        >
          Vernieuwen
        </button>
      </div>
    </div>
  );
}
